import React, { useCallback, useEffect } from 'react';

import { Form, Formik } from 'formik';

import { useNavigate } from 'react-router-dom';

import AvatarField from 'form-elements/avatar-field';
import AddressField from 'form-elements/address-field';
import TextField from 'form-elements/text-field';
import TextAreaField from 'form-elements/text-area-field';

import { createLocale } from 'locales';

import Button from 'components-new/button/button';
import Typography from 'components-new/typography/typography';
import { showErrorToast, showSuccessToast } from 'components-new/toast/toast';

import { STATIC_FILES_URL } from 'constants/signup-flow-constants';

import useDisableRefetchOnFocus from 'hooks/use-disable-refetch-on-focus';

import useUpdateFreelancerPersonalInformation from 'state/remote/remote-freelancer/use-update-freelancer-personal-information';
import useUpdateFreelancerProfilePicture from 'state/remote/remote-freelancer/use-update-freelancer-profile-picture';
import type { ErrorType } from 'state/custom-instance';

import FreelancerSignUpPageContainer from '../components/freelancer-sign-up-page-container';

import freelancerRoutes from '../freelancer-routes';

import personalInformationFormValidate from './schema';
import pictureFormValidate from './profilePictureSchema';

import dialogInstructionsContent from './dialogInstructionsContent';

const url = `${STATIC_FILES_URL}videos/signupflow/h2.mp4`;
const videoPreviewImageURL = `${window.STATIC_URL}assets/signupflow/h2.png`;

const localize = createLocale('freelancerPersonalInformationPageLocales');
const globalLocalize = createLocale('global');

const PersonalInformation: React.FC = () => {
  const navigate = useNavigate();
  useDisableRefetchOnFocus();

  const onPictureIsSuccessCallback = useCallback(() => {
    showSuccessToast({
      description: localize('profilePictureAdded'),
      title: globalLocalize('notification'),
    });
  }, []);

  const onPictureErrorCallback = useCallback(
    (error: ErrorType<unknown> | null) => {
      showErrorToast({
        description: error?.message,
        title: globalLocalize('error'),
      });
    },
    [],
  );

  const {
    currentPicture,
    initialValues: pictureInitialValues,
    isLoading: pictureIsLoading,
    updateFreelancerPicture,
  } = useUpdateFreelancerProfilePicture({
    onPictureErrorCallback,
    onPictureIsSuccessCallback,
  });

  const {
    initialValues,
    isLoading,
    updateFreelancerPersonalInformation,
    isSuccess,
    error,
  } = useUpdateFreelancerPersonalInformation();

  const navigateToFillInformationPage = useCallback(() => {
    navigate(freelancerRoutes.fillInformation.path);
  }, [navigate]);

  const navigateToCompetencesPage = useCallback(() => {
    navigate(freelancerRoutes.competenciesInformation.path);
  }, [navigate]);

  const onUpdatePersonalInformationClicked = useCallback(
    async (
      values: Parameters<typeof updateFreelancerPersonalInformation>[0],
    ) => {
      await updateFreelancerPersonalInformation(values);
    },
    [updateFreelancerPersonalInformation],
  );

  const onUpdatePictureClicked = useCallback(
    (values: Parameters<typeof updateFreelancerPicture>[0]) => {
      updateFreelancerPicture(values);
    },
    [updateFreelancerPicture],
  );

  useEffect(() => {
    if (isSuccess) {
      showSuccessToast({
        description: localize('personalInformationAdded'),
        title: globalLocalize('notification'),
      });
      navigateToCompetencesPage();
    }
    if (error) {
      showErrorToast({
        description: error.message,
        title: globalLocalize('error'),
      });
    }
  }, [error, isSuccess, navigateToCompetencesPage]);

  return (
    <FreelancerSignUpPageContainer
      showHeader
      showGoBackButton
      onGoBackClick={navigateToFillInformationPage}
      headerTitle={localize('title')}
      videoInstructions={{
        url,
        videoPreviewImageURL,
      }}
      dialogInstructions={{
        component: dialogInstructionsContent,
        shouldShowDialogInstructions: !initialValues.firstName,
      }}
    >
      <Typography variant="body-medium" as="p" className="my-4 center-text">
        {localize('needMoreInformation')}
      </Typography>

      <Formik
        initialValues={pictureInitialValues}
        enableReinitialize
        validationSchema={pictureFormValidate}
        onSubmit={onUpdatePictureClicked}
      >
        {({ values }) => {
          return (
            <Form className="w-full">
              <AvatarField
                name="picture"
                title={localize('profilePicture')}
                avatarClassName="mx-auto h-28 w-28"
              />

              {!!values.picture && values.picture !== currentPicture && (
                <Button
                  loading={pictureIsLoading}
                  disabled={pictureIsLoading}
                  label={localize('uploadProfilePicture')}
                  variant="primary"
                  type="submit"
                  className="my-3 w-full flex flex-row items-center justify-center"
                />
              )}
            </Form>
          );
        }}
      </Formik>

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={personalInformationFormValidate}
        onSubmit={onUpdatePersonalInformationClicked}
      >
        {({ errors, touched }) => (
          <Form className="mb-16 w-full flex flex-col gap-2">
            <TextField
              name="firstName"
              type="text"
              title={localize('formFirstName')}
            />

            <TextField
              name="lastName"
              type="text"
              title={localize('formLastName')}
            />

            <TextField
              name="phoneNumber"
              type="phone"
              title={localize('formPhoneNumber')}
            />

            <AddressField errors={errors} touched={touched} />

            <TextField
              name="street"
              type="text"
              title={localize('formStreet')}
            />
            <TextField name="city" type="text" title={localize('formCity')} />

            <TextAreaField
              name="about"
              title={localize('profileText')}
              placeholder={localize('profileTextPlaceholder')}
              textareaClassName="min-h-[100px]"
              rows={4}
            />

            <Button
              loading={isLoading}
              disabled={isLoading || !currentPicture}
              label={globalLocalize('continue')}
              type="submit"
              variant="primary"
              size="medium"
              className="my-3 w-full flex flex-row items-center justify-center"
            />
          </Form>
        )}
      </Formik>
    </FreelancerSignUpPageContainer>
  );
};

export default PersonalInformation;
